import React, { useMemo } from 'react';
import { useFieldArray } from 'react-hook-form';
import { Copy } from 'lucide-react';
import StepHeading, { SectionCard, InfoNotice } from '../StepHeading';
import StepFooter from '../StepFooter';
import CoverageMeter from '../CoverageMeter';
import { useStepForm } from '../useStepForm';
import { TextInput, SelectInput, MonthInput, DateInput, PhoneInput, ZipInput, SsnInput, YesNoToggle, RepeatableGroup, ErrorSummary } from '../../ui/form';
import { US_STATES } from '../../../utils/usStates';
import { RELATIONSHIP_OPTIONS } from '../stepConfig';
import { computeCoverage, validateMonthRange } from '../../../utils/applicationCoverage';
import { formatPhoneInput, phoneDigits, ssnDigits, todayIso, isValidMonth, ageOn } from '../../../utils/dateInput';

const emptyResidence = () => ({ address: '', city: '', state: '', zipCode: '', from: '', to: '' });

const toFormValues = (application) => {
  const personal = application.sections?.personal || {};
  return {
    personal: {
      firstName: personal.firstName || application.applicant?.firstName || '',
      middleName: personal.middleName || '',
      lastName: personal.lastName || application.applicant?.lastName || '',
      address: personal.address || '',
      city: personal.city || '',
      state: personal.state || '',
      zipCode: personal.zipCode || '',
      currentAddressSince: personal.currentAddressSince || '',
      dateOfBirth: personal.dateOfBirth || '',
      phone: formatPhoneInput(personal.phone || application.applicant?.phone || ''),
      ssn: '',
      hasTwicCard: typeof personal.hasTwicCard === 'boolean' ? personal.hasTwicCard : null,
      twicExpiration: personal.twicExpiration || '',
      hasPassport: typeof personal.hasPassport === 'boolean' ? personal.hasPassport : null,
      emergencyContact: {
        name: personal.emergencyContact?.name || '',
        phone: formatPhoneInput(personal.emergencyContact?.phone || ''),
        relationship: personal.emergencyContact?.relationship || ''
      }
    },
    residences: (application.sections?.residences || []).map((r) => ({ ...emptyResidence(), ...r }))
  };
};

const toPayload = (values) => {
  const personal = { ...values.personal };
  personal.phone = phoneDigits(personal.phone);
  personal.emergencyContact = { ...personal.emergencyContact, phone: phoneDigits(personal.emergencyContact?.phone) };
  const ssn = ssnDigits(personal.ssn);
  if (ssn) personal.ssn = ssn; else delete personal.ssn;
  if (!personal.hasTwicCard) personal.twicExpiration = null;
  return { personal, residences: (values.residences || []).map((r) => ({ ...r, state: (r.state || '').toUpperCase() })) };
};

const LABELS = {
  'personal.firstName': 'First name', 'personal.lastName': 'Last name', 'personal.address': 'Street address', 'personal.city': 'City',
  'personal.state': 'State', 'personal.zipCode': 'ZIP code', 'personal.currentAddressSince': 'At current address since', 'personal.dateOfBirth': 'Date of birth',
  'personal.phone': 'Phone number', 'personal.ssn': 'Social Security Number', 'personal.hasTwicCard': 'TWIC card', 'personal.hasPassport': 'Passport',
  'personal.emergencyContact.name': 'Emergency contact name', 'personal.emergencyContact.phone': 'Emergency contact phone', 'personal.emergencyContact.relationship': 'Emergency contact relationship',
  residences: 'Residence history',
  'residences.*.address': 'Previous address {n}: street', 'residences.*.city': 'Previous address {n}: city', 'residences.*.state': 'Previous address {n}: state',
  'residences.*.zipCode': 'Previous address {n}: ZIP code', 'residences.*.from': 'Previous address {n}: from', 'residences.*.to': 'Previous address {n}: to'
};

const DriverInformationStep = ({ application, locked, flaggedMessage, onSaveDraft, onComplete, onContinue, isFirst }) => {
  const defaultValues = useMemo(() => toFormValues(application), [application.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const hasSsnOnFile = Boolean(application.sections?.personal?.ssnLast4);

  const validate = (values) => {
    const problems = [];
    const dob = values.personal?.dateOfBirth;
    if (dob) {
      const age = ageOn(dob);
      if (age !== null && age < 18) problems.push({ name: 'personal.dateOfBirth', message: 'You must be at least 18 years old to apply' });
    }
    if (!hasSsnOnFile && ssnDigits(values.personal?.ssn).length !== 9) {
      problems.push({ name: 'personal.ssn', message: 'Enter your 9-digit Social Security Number' });
    } else if (values.personal?.ssn && ssnDigits(values.personal.ssn).length !== 9) {
      problems.push({ name: 'personal.ssn', message: 'Enter all 9 digits, or leave blank to keep the number on file' });
    }
    if (values.personal?.currentAddressSince) {
      const problem = validateMonthRange({ from: values.personal.currentAddressSince, isPresent: true });
      if (problem) problems.push({ name: 'personal.currentAddressSince', message: problem });
    }
    (values.residences || []).forEach((r, index) => {
      const problem = validateMonthRange({ from: r.from, to: r.to });
      if (problem) problems.push({ name: `residences.${index}.from`, message: problem });
    });
    if (!problems.length) {
      const coverage = computeCoverage({
        entries: [values.personal?.currentAddressSince ? { from: values.personal.currentAddressSince, isPresent: true } : null, ...(values.residences || []).map((r) => ({ from: r.from, to: r.to }))].filter(Boolean),
        requiredMonths: 36
      });
      const gap = coverage.gapsNeedingExplanation[0];
      if (gap) problems.push({ name: 'residences', message: `Add the address where you lived from ${gap.from} to ${gap.to}` });
    }
    return problems;
  };

  const { form, values, errors, autosave, submitting, serverMessage, failedAttempts, submitNext } = useStepForm({
    stepId: 'personal', defaultValues, toPayload, onSaveDraft, onComplete, locked, validate
  });
  const { register, control, setValue, getValues } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'residences' });

  const coverage = useMemo(() => computeCoverage({
    entries: [
      isValidMonth(values.personal?.currentAddressSince) ? { from: values.personal.currentAddressSince, isPresent: true } : null,
      ...(values.residences || []).filter((r) => isValidMonth(r.from) && isValidMonth(r.to)).map((r) => ({ from: r.from, to: r.to }))
    ].filter(Boolean),
    requiredMonths: 36
  }), [values.personal?.currentAddressSince, values.residences]);

  const copyCurrentAddress = (index) => {
    const p = getValues('personal');
    setValue(`residences.${index}.address`, p.address || '', { shouldDirty: true });
    setValue(`residences.${index}.city`, p.city || '', { shouldDirty: true });
    setValue(`residences.${index}.state`, p.state || '', { shouldDirty: true });
    setValue(`residences.${index}.zipCode`, p.zipCode || '', { shouldDirty: true });
  };

  const disabled = locked;
  const today = todayIso();

  return (
    <form onSubmit={(e) => { e.preventDefault(); submitNext(); }} noValidate>
      <StepHeading title="Driver Information" description="Tell us who you are and where you have lived for the last 3 years." flaggedMessage={flaggedMessage} locked={locked} />
      <ErrorSummary errors={errors} labels={LABELS} focusKey={failedAttempts} className="mb-5" />
      {serverMessage && !Object.keys(errors).length && <p className="mb-4 text-sm text-red-600" role="alert">{serverMessage}</p>}

      <fieldset disabled={disabled} className="space-y-5">
        <SectionCard title="About you">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput label="First name" required autoComplete="given-name" {...register('personal.firstName', { required: 'First name is required' })} error={errors.personal?.firstName?.message} />
            <TextInput label="Last name" required autoComplete="family-name" {...register('personal.lastName', { required: 'Last name is required' })} error={errors.personal?.lastName?.message} />
            <TextInput label="Middle name" optionalLabel autoComplete="additional-name" {...register('personal.middleName')} />
            <DateInput label="Date of birth" required max={today} autoComplete="bday" {...register('personal.dateOfBirth', { required: 'Date of birth is required' })} error={errors.personal?.dateOfBirth?.message} />
            <PhoneInput label="Phone number" required {...register('personal.phone', { required: 'Phone number is required', validate: (v) => phoneDigits(v).length === 10 || 'Enter a 10-digit phone number' })} error={errors.personal?.phone?.message} />
            <TextInput label="Email" value={application.applicant?.email || ''} readOnly hint="Locked to the address your invite was sent to" inputClassName="bg-gray-50 text-gray-600" />
            <SsnInput
              required={!hasSsnOnFile}
              last4={application.sections?.personal?.ssnLast4}
              hint={hasSsnOnFile ? 'Already on file. Enter it again only if it needs to change.' : 'Stored encrypted. Required for employment verification.'}
              {...register('personal.ssn')}
              error={errors.personal?.ssn?.message}
            />
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <YesNoToggle name="personal.hasTwicCard" control={control} label="Do you have a TWIC card?" required />
            <YesNoToggle name="personal.hasPassport" control={control} label="Do you have a passport?" required />
            {values.personal?.hasTwicCard === true && (
              <DateInput label="TWIC card expiration" optionalLabel min={today} {...register('personal.twicExpiration')} error={errors.personal?.twicExpiration?.message} />
            )}
          </div>
        </SectionCard>

        <SectionCard title="Current address">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextInput label="Street address" required autoComplete="street-address" className="md:col-span-2" {...register('personal.address', { required: 'Street address is required' })} error={errors.personal?.address?.message} />
            <TextInput label="City" required autoComplete="address-level2" {...register('personal.city', { required: 'City is required' })} error={errors.personal?.city?.message} />
            <SelectInput label="State" required autoComplete="address-level1" options={US_STATES} {...register('personal.state', { required: 'State is required' })} error={errors.personal?.state?.message} />
            <ZipInput label="ZIP code" required {...register('personal.zipCode', { required: 'ZIP code is required', pattern: { value: /^\d{5}(-\d{4})?$/, message: 'Enter a 5-digit ZIP code' } })} error={errors.personal?.zipCode?.message} />
            <MonthInput label="Living here since" required hint="Month and year you moved in" {...register('personal.currentAddressSince', { required: 'Tell us when you moved to this address', validate: (v) => isValidMonth(v) || 'Choose the month and year you moved in' })} error={errors.personal?.currentAddressSince?.message} />
          </div>
        </SectionCard>

        <SectionCard title="Emergency contact" description="Who should we call in an emergency?">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <TextInput label="Name" required {...register('personal.emergencyContact.name', { required: 'Emergency contact name is required' })} error={errors.personal?.emergencyContact?.name?.message} />
            <PhoneInput label="Phone" required {...register('personal.emergencyContact.phone', { required: 'Emergency contact phone is required', validate: (v) => phoneDigits(v).length === 10 || 'Enter a 10-digit phone number' })} error={errors.personal?.emergencyContact?.phone?.message} />
            <SelectInput label="Relationship" required options={RELATIONSHIP_OPTIONS} {...register('personal.emergencyContact.relationship', { required: 'Choose a relationship' })} error={errors.personal?.emergencyContact?.relationship?.message} />
          </div>
        </SectionCard>

        <SectionCard title="Previous addresses, last 3 years" description="Federal rules (49 CFR 391.21) require every address you have lived at during the past three years.">
          <CoverageMeter coverage={coverage} label="Residence history" className="mb-4" />
          {coverage.isFullyCovered && fields.length === 0 ? (
            <InfoNotice>You have lived at your current address for 3 or more years, so no previous addresses are needed. Add one anyway if you moved during that time.</InfoNotice>
          ) : null}
          {errors.residences?.message && <p className="mb-3 text-sm text-red-600" role="alert">{errors.residences.message}</p>}
          <RepeatableGroup
            items={fields}
            title={(i) => `Previous address ${i + 1}`}
            addLabel="Add a previous address"
            onAdd={() => append(emptyResidence())}
            onRemove={remove}
            disabled={disabled}
            emptyHint={coverage.isFullyCovered ? null : 'Add each address you lived at before your current one.'}
            renderItem={(index) => (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2 flex justify-end">
                  <button type="button" onClick={() => copyCurrentAddress(index)} className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900">
                    <Copy className="h-3.5 w-3.5" aria-hidden="true" /> Same as current address
                  </button>
                </div>
                <TextInput label="Street address" required className="md:col-span-2" {...register(`residences.${index}.address`, { required: 'Street address is required' })} error={errors.residences?.[index]?.address?.message} />
                <TextInput label="City" required {...register(`residences.${index}.city`, { required: 'City is required' })} error={errors.residences?.[index]?.city?.message} />
                <SelectInput label="State" required options={US_STATES} {...register(`residences.${index}.state`, { required: 'State is required' })} error={errors.residences?.[index]?.state?.message} />
                <ZipInput label="ZIP code" required {...register(`residences.${index}.zipCode`, { required: 'ZIP code is required', pattern: { value: /^\d{5}(-\d{4})?$/, message: 'Enter a 5-digit ZIP code' } })} error={errors.residences?.[index]?.zipCode?.message} />
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                  <MonthInput label="From" required {...register(`residences.${index}.from`, { required: 'Required', validate: (v) => isValidMonth(v) || 'Choose a month and year' })} error={errors.residences?.[index]?.from?.message} />
                  <MonthInput label="To" required {...register(`residences.${index}.to`, { required: 'Required', validate: (v) => isValidMonth(v) || 'Choose a month and year' })} error={errors.residences?.[index]?.to?.message} />
                </div>
              </div>
            )}
          />
        </SectionCard>
      </fieldset>

      <StepFooter
        isFirst={isFirst}
        onNext={locked ? onContinue : submitNext}
        isSubmitting={submitting}
        saveStatus={autosave.status}
        lastSavedAt={autosave.lastSavedAt}
        onRetrySave={autosave.saveNow}
        nextDisabled={locked && !onContinue}
        nextLabel={locked ? 'Continue' : 'Save & continue'}
      />
    </form>
  );
};

export default DriverInformationStep;
